"use client";

// ── A run-twice submission's result (`EX-RTW03`, board F) ──────────────────
// Two pieces: the line under the verdict that says which stage the failing
// test stopped in, with each run's longest time; and, where the feedback
// policy names tests (practice, a staff rehearsal, the moderator's sheet),
// the per-test table with a Run 1, a Jury and a Run 2 cell. A contest's
// `count_only` row carries the line only, from the server's summary, which
// names no test. Both render nothing on a standard problem.

import { Check, Minus, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format-datetime";
import type { StudentTestResult } from "@/features/quiz/coding-task-api";
import { runTwiceSummaryOf, stageCellsOf, type RunTwiceSummary, type StageCell } from "../../lib/run-twice";
import { formatMegabytes } from "./TestStrip";
import { formatShare, partialShareOf } from "../../lib/partial-points";

type Tone = "dark" | "light";

export function RunTwiceStageLine({
  summary,
  tone,
  locale,
}: {
  summary: RunTwiceSummary;
  tone: Tone;
  locale: string;
}) {
  const t = useTranslations("judge.runTwice");
  const tEditor = useTranslations("judge.editor");
  const runs = summary.runs
    .slice(0, 2)
    .map((run, index) => ({
      label: index === 0 ? t("longestRun1") : t("longestRun2"),
      value: [
        run.timeMs !== undefined ? tEditor("figureMs", { ms: run.timeMs }) : null,
        run.memoryKb !== undefined ? tEditor("figureMb", { mb: formatMegabytes(run.memoryKb, locale) }) : null,
      ]
        .filter((part): part is string => part !== null)
        .join(" · "),
    }))
    .filter((run) => run.value !== "");

  return (
    <div className="mt-3 space-y-2" data-testid="run-twice-result-line">
      {summary.stage ? (
        <p
          className={cn(
            "rounded-lg border px-3 py-2 text-xs leading-relaxed",
            tone === "dark" ? "border-red-400/25 bg-red-400/10 text-red-100" : "border-red-200 bg-red-50 text-red-800",
          )}
          data-stage={summary.stage}
        >
          {t(`resultStage_${summary.stage}`)}
        </p>
      ) : null}
      {runs.length > 0 ? (
        <dl className="grid grid-cols-2 gap-2">
          {runs.map((run) => (
            <div
              key={run.label}
              className={cn("min-w-0 rounded-lg px-3 py-2", tone === "dark" ? "bg-white/5" : "bg-neutral-50 ring-1 ring-neutral-200")}
            >
              <dt className={cn("truncate text-[11px]", tone === "dark" ? "text-white/50" : "text-neutral-500")}>{run.label}</dt>
              <dd className={cn("truncate text-sm font-semibold tabular-nums", tone === "dark" ? "text-white" : "text-neutral-900")}>
                {run.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

const CELL_TONE: Readonly<Record<Tone, Record<StageCell, string>>> = {
  dark: {
    pass: "bg-emerald-400/20 text-emerald-300",
    fail: "bg-red-400/20 text-red-300",
    skip: "bg-white/5 text-white/30",
  },
  light: {
    pass: "bg-emerald-600 text-white",
    fail: "bg-red-600 text-white",
    skip: "bg-neutral-200 text-neutral-500",
  },
};

function Cell({ cell, tone, label, time }: { cell: StageCell; tone: Tone; label: string; time?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn("inline-flex h-[18px] w-[18px] items-center justify-center rounded-full", CELL_TONE[tone][cell])}
        role="img"
        aria-label={label}
        data-cell={cell}
      >
        {cell === "pass" ? (
          <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
        ) : cell === "fail" ? (
          <X className="h-3 w-3" strokeWidth={3} aria-hidden />
        ) : (
          <Minus className="h-3 w-3" strokeWidth={3} aria-hidden />
        )}
      </span>
      {time ? <span className={cn("text-[11px] tabular-nums", tone === "dark" ? "text-white/45" : "text-neutral-500")}>{time}</span> : null}
    </span>
  );
}

export function RunTwiceTestTable({
  rows,
  tone,
  locale,
}: {
  rows: readonly StudentTestResult[];
  tone: Tone;
  locale: string;
}) {
  const t = useTranslations("judge.runTwice");
  const tJudge = useTranslations("judge");
  const tEditor = useTranslations("judge.editor");
  const cellLabel = (cell: StageCell) => (cell === "pass" ? t("cellPass") : cell === "fail" ? t("cellFail") : t("cellSkip"));
  const timeOf = (row: StudentTestResult, index: number) => {
    const ms = row.runs?.[index]?.timeMs;
    return ms !== undefined ? tEditor("figureMs", { ms }) : undefined;
  };
  const head = tone === "dark" ? "text-white/50" : "text-neutral-500";

  return (
    <div className="mt-3">
      <div
        className={cn(
          "max-h-56 overflow-auto rounded-lg",
          tone === "dark" ? "ring-1 ring-inset ring-white/10" : "border border-neutral-200",
        )}
      >
        <table className="w-full min-w-[300px] border-collapse text-xs" aria-label={t("tableLabel")} data-testid="run-twice-table">
          <thead className={cn("sticky top-0", tone === "dark" ? "bg-neutral-900" : "bg-neutral-50")}>
            <tr className={cn("text-left text-[11px] font-semibold", head)}>
              <th scope="col" className="px-2.5 py-1.5">{t("colTest")}</th>
              <th scope="col" className="px-2.5 py-1.5">{t("colRun1")}</th>
              <th scope="col" className="px-2.5 py-1.5">{t("colJury")}</th>
              <th scope="col" className="px-2.5 py-1.5">{t("colRun2")}</th>
              <th scope="col" className="px-2.5 py-1.5">{t("colVerdict")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const cells = stageCellsOf(row);
              if (!cells) return null;
              const failed = row.verdict !== "accepted" && row.verdict !== "skipped";
              return (
                <tr
                  key={row.idx}
                  className={cn(
                    "border-t",
                    tone === "dark" ? "border-white/5" : "border-neutral-100",
                    failed && (tone === "dark" ? "bg-red-400/[0.06]" : "bg-red-50/60"),
                  )}
                  data-testid={`run-twice-row-${row.idx}`}
                >
                  <td className={cn("px-2.5 py-1.5 font-mono tabular-nums", head)}>{formatNumber(row.idx, locale)}</td>
                  <td className="px-2.5 py-1.5">
                    <Cell cell={cells.first} tone={tone} label={cellLabel(cells.first)} time={cells.first !== "skip" ? timeOf(row, 0) : undefined} />
                  </td>
                  <td className="px-2.5 py-1.5">
                    <Cell cell={cells.bridge} tone={tone} label={cellLabel(cells.bridge)} />
                  </td>
                  <td className="px-2.5 py-1.5">
                    <Cell cell={cells.second} tone={tone} label={cellLabel(cells.second)} time={cells.second !== "skip" ? timeOf(row, 1) : undefined} />
                  </td>
                  <td
                    className={cn(
                      "whitespace-nowrap px-2.5 py-1.5 font-semibold",
                      row.verdict === "accepted"
                        ? tone === "dark" ? "text-emerald-300" : "text-emerald-700"
                        : partialShareOf(row) !== null
                          ? tone === "dark" ? "text-amber-200" : "text-amber-700"
                          : row.verdict === "skipped"
                          ? head
                          : tone === "dark" ? "text-red-300" : "text-red-700",
                    )}
                  >
                    {partialShareOf(row) !== null
                      ? tJudge("partialShare", { share: formatShare(row.share ?? 0, locale) })
                      : tJudge(`verdict.${row.verdict}`)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={cn("mt-1.5 text-[11px]", head)}>{t("legend")}</p>
    </div>
  );
}

/**
 * The light detail's whole run-twice block (the moderator's sheet, a lesson's
 * rows never reach it): the stage line and the table, in the page's locale.
 * Its own component so the locale is read only where a run-twice row is.
 */
export function RunTwiceDetail({ rows }: { rows: readonly StudentTestResult[] }) {
  const locale = useLocale();
  const summary = runTwiceSummaryOf({ testResults: rows });
  return (
    <>
      {summary ? <RunTwiceStageLine summary={summary} tone="light" locale={locale} /> : null}
      <RunTwiceTestTable rows={rows} tone="light" locale={locale} />
    </>
  );
}
